/**
 * MaintenanceTab - Pestaña de mantenimiento e intervenciones
 *
 * @version 1.0
 * @updated 2026-02-12
 * @module core/map-tools/TreeDetailsPanel
 */

import type { Tree } from '../../tree-types';
import { FormLabel } from '../FormLabel';

export interface MaintenanceTabProps {
  tree: Tree;
  onChange: (field: keyof Tree, value: any) => void;
}

export function MaintenanceTab({ tree, onChange }: MaintenanceTabProps): JSX.Element {
  // Handler para los tipos de intervención (multi-select)
  const handleInterventionTypeChange = (value: string, checked: boolean) => {
    const currentTypes = tree.interventionType || [];
    const newTypes = checked
      ? [...currentTypes, value]
      : currentTypes.filter(t => t !== value);
    onChange('interventionType', newTypes);
  };

  return (
    <div className="tree-tab-panel">
      {/* Sección: Necesidades Actuales */}
      <div className="tree-form-section">
        <h3 className="tree-form-section-title">Necesidades Actuales</h3>

        <div className="tree-form-checkbox-group">
          <input
            type="checkbox"
            id="needsIntervention"
            className="tree-form-checkbox"
            checked={tree.needsIntervention}
            onChange={(e) => onChange('needsIntervention', e.target.checked)}
          />
          <label htmlFor="needsIntervention" className="tree-form-checkbox-label">
            Necesita intervención
          </label>
        </div>

        {tree.needsIntervention && (
          <>
            <div className="tree-form-group">
              <FormLabel tooltip="Tipo de trabajo requerido. Poda: corte de ramas. Riego: en épocas secas. Fertilización: nutrientes. Tratamiento: control de plagas/enfermedades. Extracción: remoción completa. Apuntalamiento: soporte estructural.">
                Tipo de intervención (seleccionar todas las necesarias)
              </FormLabel>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '8px' }}>
                {[
                  { value: 'poda', label: 'Poda' },
                  { value: 'riego', label: 'Riego' },
                  { value: 'fertilización', label: 'Fertilización' },
                  { value: 'tratamiento', label: 'Tratamiento fitosanitario' },
                  { value: 'extracción', label: 'Extracción' },
                  { value: 'apuntalamiento', label: 'Apuntalamiento' },
                  { value: 'otro', label: 'Otro' },
                ].map(({ value, label }) => (
                  <div key={value} className="tree-form-checkbox-group">
                    <input
                      type="checkbox"
                      id={`intervention-${value}`}
                      className="tree-form-checkbox"
                      checked={(tree.interventionType ?? []).includes(value)}
                      onChange={(e) => handleInterventionTypeChange(value, e.target.checked)}
                    />
                    <label htmlFor={`intervention-${value}`} className="tree-form-checkbox-label">
                      {label}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            <div className="tree-form-group">
              <FormLabel tooltip="Urgencia de la intervención. Baja: rutinaria. Media: programar en trimestre. Alta: realizar en mes. Urgente: acción inmediata requerida por riesgo.">
                Prioridad
              </FormLabel>
              <select
                className="tree-form-select"
                value={tree.priority}
                onChange={(e) => onChange('priority', e.target.value)}
              >
                <option value="">Seleccionar...</option>
                <option value="low">Baja</option>
                <option value="medium">Media</option>
                <option value="high">Alta</option>
                <option value="urgent">Urgente</option>
              </select>
            </div>

            <div className="tree-form-group">
              <FormLabel tooltip="Fecha programada para realizar la próxima intervención o mantenimiento planificado del árbol.">
                Próxima intervención programada
              </FormLabel>
              <input
                type="date"
                className="tree-form-input"
                value={tree.scheduledIntervention ? tree.scheduledIntervention.toISOString().split('T')[0] : ''}
                onChange={(e) => onChange('scheduledIntervention', e.target.value ? new Date(e.target.value) : undefined)}
              />
            </div>

            <div className="tree-form-group">
              <FormLabel tooltip="Persona, cuadrilla o empresa asignada para realizar la intervención programada. Facilita el seguimiento y responsabilidades.">
                Responsable asignado
              </FormLabel>
              <input
                type="text"
                className="tree-form-input"
                value={tree.assignedResponsible}
                onChange={(e) => onChange('assignedResponsible', e.target.value)}
                placeholder="Ej: Cuadrilla 3, Empresa XYZ"
              />
            </div>
          </>
        )}
      </div>

      {/* Sección: Historial de Intervenciones */}
      <div className="tree-form-section">
        <h3 className="tree-form-section-title">Historial de Intervenciones</h3>

        {/* Poda */}
        <div className="tree-form-group">
          <FormLabel tooltip="Fecha de la última poda realizada. La frecuencia de poda varía según especie, ubicación y objetivos (formación, limpieza, reducción, etc.).">
            Última fecha de poda
          </FormLabel>
          <input
            type="date"
            className="tree-form-input"
            value={tree.lastPruningDate ? tree.lastPruningDate.toISOString().split('T')[0] : ''}
            onChange={(e) => onChange('lastPruningDate', e.target.value ? new Date(e.target.value) : undefined)}
          />
        </div>

        {tree.lastPruningDate && (
          <div className="tree-form-group">
            <FormLabel tooltip="Formación: dar estructura en árboles jóvenes. Limpieza: eliminar ramas secas/rotas. Reducción: disminuir tamaño. Saneamiento: eliminar partes enfermas. Raleo: abrir la copa para luz/aire.">
              Tipo de poda realizada
            </FormLabel>
            <select
              className="tree-form-select"
              value={tree.lastPruningType}
              onChange={(e) => onChange('lastPruningType', e.target.value)}
            >
              <option value="">Seleccionar...</option>
              <option value="formative">Formación</option>
              <option value="cleaning">Limpieza</option>
              <option value="reduction">Reducción</option>
              <option value="health">Saneamiento</option>
              <option value="thinning">Raleo</option>
            </select>
          </div>
        )}

        {/* Fertilización */}
        <div className="tree-form-group">
          <FormLabel tooltip="Fecha de la última aplicación de fertilizantes o nutrientes. Generalmente se realiza en primavera o cuando se detectan deficiencias nutricionales.">
            Última fecha de fertilización
          </FormLabel>
          <input
            type="date"
            className="tree-form-input"
            value={tree.lastFertilizationDate ? tree.lastFertilizationDate.toISOString().split('T')[0] : ''}
            onChange={(e) => onChange('lastFertilizationDate', e.target.value ? new Date(e.target.value) : undefined)}
          />
        </div>

        {tree.lastFertilizationDate && (
          <div className="tree-form-group">
            <FormLabel tooltip="Nombre comercial o composición del fertilizante aplicado. NPK indica proporciones de Nitrógeno-Fósforo-Potasio (ej: 15-15-15 tiene partes iguales).">
              Producto utilizado
            </FormLabel>
            <input
              type="text"
              className="tree-form-input"
              value={tree.lastFertilizationProduct}
              onChange={(e) => onChange('lastFertilizationProduct', e.target.value)}
              placeholder="Ej: NPK 15-15-15, Compost orgánico"
            />
          </div>
        )}

        {/* Riego */}
        <div className="tree-form-group">
          <FormLabel tooltip="Fecha del último riego registrado. El riego suplementario es crítico en épocas de sequía, especialmente para árboles jóvenes o recién plantados.">
            Última fecha de riego
          </FormLabel>
          <input
            type="date"
            className="tree-form-input"
            value={tree.lastIrrigationDate ? tree.lastIrrigationDate.toISOString().split('T')[0] : ''}
            onChange={(e) => onChange('lastIrrigationDate', e.target.value ? new Date(e.target.value) : undefined)}
          />
        </div>

        {/* Tratamiento Fitosanitario */}
        <div className="tree-form-group">
          <FormLabel tooltip="Fecha del último tratamiento para control de plagas o enfermedades. Incluye aplicaciones de insecticidas, fungicidas, acaricidas u otros productos fitosanitarios.">
            Última fecha de tratamiento fitosanitario
          </FormLabel>
          <input
            type="date"
            className="tree-form-input"
            value={tree.lastTreatmentDate ? tree.lastTreatmentDate.toISOString().split('T')[0] : ''}
            onChange={(e) => onChange('lastTreatmentDate', e.target.value ? new Date(e.target.value) : undefined)}
          />
        </div>

        {tree.lastTreatmentDate && (
          <>
            <div className="tree-form-group">
              <FormLabel tooltip="Nombre comercial o principio activo del producto fitosanitario aplicado. Importante para seguimiento de eficacia y trazabilidad.">
                Producto usado
              </FormLabel>
              <input
                type="text"
                className="tree-form-input"
                value={tree.lastTreatmentProduct}
                onChange={(e) => onChange('lastTreatmentProduct', e.target.value)}
                placeholder="Ej: Insecticida XYZ, Fungicida ABC"
              />
            </div>

            <div className="tree-form-group">
              <FormLabel tooltip="Organismo objetivo del tratamiento. Identificar correctamente la plaga o enfermedad es fundamental para elegir el producto y método de control apropiado.">
                Plaga/Enfermedad tratada
              </FormLabel>
              <input
                type="text"
                className="tree-form-input"
                value={tree.lastTreatmentTarget}
                onChange={(e) => onChange('lastTreatmentTarget', e.target.value)}
                placeholder="Ej: Pulgones, Hongo de tronco"
              />
            </div>
          </>
        )}

        {/* Historial completo */}
        <div className="tree-form-group">
          <FormLabel tooltip="Registro cronológico completo de todas las intervenciones realizadas en el árbol. Incluir fecha, tipo de trabajo, productos usados, dosis, responsable y resultados observados. Clave para seguimiento a largo plazo.">
            Historial completo de tratamientos
          </FormLabel>
          <textarea
            className="tree-form-textarea"
            value={tree.treatmentHistory}
            onChange={(e) => onChange('treatmentHistory', e.target.value)}
            placeholder="Registro detallado de todas las intervenciones realizadas..."
            rows={6}
          />
          <div className="tree-form-hint">
            Incluir fecha, tipo de intervención, productos, dosis, responsable, resultados observados, etc.
          </div>
        </div>
      </div>
    </div>
  );
}
